import { AlertTriangle, CircleCheckBig, CircleDashed, Loader2, Warehouse } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/workspace-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { CoverageBlock } from '../utils/creationCoverage';
import { planKey, type ItemPlan } from './itemPlan';

interface CreationReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  /** Items that will be picked. An out-of-stock item never reaches here: Create is disabled until it is removed. */
  plans: ItemPlan[];
  /** PT-7 E3 — each MRS in the selection, and how much of it this picklist takes (`creationCoverage`). */
  coverage: CoverageBlock[];
  /** How many bins the user chose per plan, keyed by `planKey` (item AND unit, O-22). */
  chosenBinCounts: Record<string, number>;
  onViewBins: (plan: ItemPlan) => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  submitError: string | null;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** `MRS-100028015 ×2` when one request raised the item twice. */
function mrsChipLabels(plan: ItemPlan): string[] {
  return plan.mrsNumbers.map((mrs) => {
    const count = plan.lines.filter((line) => line.item.mrsNumber === mrs).length;
    return count > 1 ? `${mrs} ×${count}` : mrs;
  });
}

/** One of the two headline figures. */
function Figure({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-[9px] border border-border bg-muted/35 px-[11px] py-[9px]">
      <div className="text-[10px] uppercase tracking-[0.05em] text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-[19px] font-bold tabular-nums">{value}</div>
    </div>
  );
}

/** The coverage pill: the whole request, or how much of it (the O-20 settlement's words). */
function coveragePill(block: CoverageBlock) {
  const left = block.left.length;
  if (left === 0) {
    return {
      tone: 'success' as const,
      Icon: CircleCheckBig,
      label: 'Whole request',
      tip: `Every line of ${block.mrsNumber} is in this picklist.`,
    };
  }
  const pct = Math.round((block.inLines / Math.max(block.totalLines, 1)) * 100);
  return {
    tone: 'warning' as const,
    Icon: CircleDashed,
    label: `${pct}% covered`,
    tip:
      left === 1
        ? `1 line of ${block.mrsNumber} is not ticked — it stays pending for another picklist.`
        : `${left} lines of ${block.mrsNumber} are not ticked — they stay pending for another picklist.`,
  };
}

/**
 * MRS coverage (PT-7 E3): how much of each request this picklist takes, so an unticked line cannot go
 * unnoticed. Its pills and chips repeat per MRS and per line, so their explanations are native titles, not
 * a Radix root each (UI-GUIDELINES §8/§9).
 */
function CoverageCard({ coverage }: { coverage: CoverageBlock[] }) {
  if (coverage.length === 0) return null;
  const partial = coverage.filter((b) => b.left.length > 0).length;
  const total = coverage.length;
  const summary = partial > 0
    ? `${partial} of ${total} ${plural(total, 'request', 'requests')} partly covered`
    : `${total} ${plural(total, 'request', 'requests')} fully covered`;

  return (
    <div className="overflow-hidden rounded-[9px] border border-border">
      <div className="flex items-center justify-between gap-2 border-b border-border/60 bg-muted/35 px-[11px] py-2">
        <span className="text-[10px] uppercase tracking-[0.05em] text-muted-foreground">MRS coverage</span>
        <span className={cn('text-[11px] font-semibold', partial > 0 ? 'text-warning' : 'text-success')}>{summary}</span>
      </div>
      {coverage.map((block) => {
        const pill = coveragePill(block);
        return (
          <div key={block.mrsId} className="border-b border-border/50 px-[11px] py-2 last:border-b-0">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <span className="font-mono text-[11.5px] font-semibold text-info">{block.mrsNumber}</span>
                <div className="truncate text-[11px] text-muted-foreground" title={block.requestorFullName}>
                  {block.requestorFullName}
                </div>
              </div>
              <span
                title={pill.tip}
                className={cn(
                  'inline-flex h-5 shrink-0 cursor-help items-center gap-1 whitespace-nowrap rounded-full px-2 text-[10.5px] font-semibold',
                  pill.tone === 'success' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning',
                )}
              >
                <pill.Icon className="h-2.5 w-2.5" aria-hidden />
                {pill.label}
              </span>
            </div>
            <div className="mt-[5px] flex flex-wrap gap-x-2.5 gap-y-[3px] text-[11px] tabular-nums text-muted-foreground">
              <span>
                <b className="font-semibold text-foreground">{block.inLines}</b> of {block.totalLines} lines in this
                picklist
              </span>
              {block.left.length > 0 && <span className="text-warning">{block.left.length} not included</span>}
            </div>
            {block.left.length > 0 && (
              <div className="mt-[5px] flex flex-wrap gap-1">
                {block.left.map((line, i) => (
                  <span
                    key={`${line.itemCode}-${i}`}
                    title={`${line.itemName} — not in this picklist`}
                    className="inline-flex h-[18px] cursor-help items-center gap-1 rounded-md border border-dashed border-warning/50 px-[7px] font-mono text-[10.5px] font-medium text-foreground"
                  >
                    {line.itemCode}
                    <span className="font-sans text-muted-foreground">
                      {line.quantity} {line.unitOfMeasure}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The creation confirmation, as a review rather than a receipt (14ykpqtnxbv §8; v2, PT-7 E).
 *
 * ⚠️ **Built from the prototype's own markup, not from a brief's prose description of it.** Read the handoff's
 * `Picklist Workspace v2.dc.html` (confirm L857-985) before changing anything here.
 *
 * Owner ruling 2026-09-30, over the prototype: the "N picklist lines will be created — one per item…" sentence
 * and the bins note under the coverage card are gone. The first repeated the Items figure; the second repeated
 * each row's "N bins chosen / Automatic" under its View bins button. Do not restore them from the design file.
 *
 * Quantities are deliberately NOT editable here: a confirmation you can change stops being a confirmation.
 * The field stays on the cart card, beside the remarks that explain it.
 */
export function CreationReviewDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  plans,
  coverage,
  chosenBinCounts,
  onViewBins,
  onConfirm,
  isSubmitting,
  submitError,
}: CreationReviewDialogProps) {
  const unitCount = plans.reduce((sum, plan) => sum + plan.total, 0);
  const shortPlans = plans.filter((plan) => plan.shortfall > 0);
  const itemWord = (n: number) => plural(n, 'item', 'items');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        The BODY is the single scroller. Do not give the two columns their own inner scrollers: in a
        wrapping flex line the cross size comes from the content, the columns take their natural
        height, and the inner scrollers never engage — the last row then paints under the footer with
        no gesture that reaches it.
        The prototype's shell is `width:100%` in a 24px-padded scrim; the old `max-w-6xl` cap is dropped
        (O-20 settlement).
      */}
      <DialogContent
        overlayClassName="bg-black/50"
        className="flex max-h-[calc(100%-3rem)] w-[calc(100%-3rem)] max-w-none flex-col gap-0 p-0"
      >
        <DialogHeader className="flex-none space-y-0 px-5 pb-0 pt-5 text-left">
          <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
          <DialogDescription className="!mt-[5px] text-[12.5px]">{description}</DialogDescription>
        </DialogHeader>

        <div className="review-cq-body min-h-0 flex-1 content-start items-start gap-4 overflow-y-auto px-5 pb-1 pt-[14px]">
          {/* Left — the figures and what they mean */}
          <div className="flex min-w-[min(260px,100%)] flex-[0_1_300px] flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">
              <Figure label="Items" value={plans.length} />
              <Figure label="Units to pick" value={unitCount} />
            </div>

            <CoverageCard coverage={coverage} />

            {shortPlans.length > 0 && (
              <div className="rounded-[9px] bg-warning/10 px-[11px] py-[9px]">
                <div className="flex items-start gap-[7px]">
                  <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-warning" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold">
                      {shortPlans.length} {itemWord(shortPlans.length)} will be picked short
                    </div>
                    <div className="mt-[5px] flex flex-col gap-[3px]">
                      {shortPlans.map((plan) => (
                        <div
                          key={planKey(plan)}
                          className="flex items-baseline justify-between gap-2.5 text-[11.5px]"
                        >
                          <span className="min-w-0 truncate font-mono" title={plan.itemCode}>{plan.itemCode}</span>
                          <span className="flex-none font-semibold tabular-nums">
                            {plan.total} of {plan.wanted} {plan.unitOfMeasure}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-1.5 text-[11px] leading-[1.45] text-muted-foreground">
                      They stay on the picklist — the pickers see the short quantity, and the balance
                      waits for stock.
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Right — one row per item, in a table rather than a stack of cards.
              ⚠️ `overflow-x-auto` makes this box the sticky header's scroll container, and the box never
              scrolls vertically, so the header stops sticking when the body scrolls. Built as the prototype
              draws it (L948) [OWNER O-45]; without it a narrow column scrolled the whole review sideways. */}
          <div className="flex min-w-[min(300px,100%)] flex-[1_1_380px] flex-col overflow-x-auto rounded-[9px] border border-border">
            {/* Opaque, so rows do not show through as they scroll under it.
                ⚠️ `bg-card` (colour) and the gradient (image) must stay as TWO classes — see the
                same header in BinAllocationDialog for why collapsing them silently removes the
                background entirely. */}
            <div className="sticky top-[-4px] z-[5] grid h-[34px] flex-none grid-cols-[minmax(150px,1.6fr)_74px_84px_84px_96px] items-center gap-2.5 rounded-t-lg border-b border-border bg-card bg-[linear-gradient(hsl(var(--muted)/0.4),hsl(var(--muted)/0.4))] px-3 text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-foreground shadow-[0_2px_4px_-1px_hsl(var(--foreground)/0.08)]">
              <span>Item</span>
              <span>From</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="cursor-help text-right">MRS qty</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs normal-case tracking-normal">
                  What the MRS lines asked for in total — across every picklist, not just this one.
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="cursor-help text-right">To pick</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs normal-case tracking-normal">
                  What this picklist will pick for the item.
                </TooltipContent>
              </Tooltip>
              <span className="text-right">Bins</span>
            </div>

            <div>
              {plans.map((plan) => {
                const chosen = chosenBinCounts[planKey(plan)];
                // The lines that will be written: a line planned at zero is dropped from the payload.
                const lineCount = plan.lines.filter((l) => l.pick > 0).length;
                // Display only: what the MRS lines asked for — the plan itself is built on availableQuantity
                // (owner ruling 2026-09-18).
                const mrsQty = plan.lines.reduce((sum, l) => sum + l.item.requestedQuantity, 0);
                const binsTip = chosen
                  ? `View or change which stock goes out for ${plan.itemCode}`
                  : `View the automatic allocation for ${plan.itemCode}`;

                return (
                  <div
                    key={planKey(plan)}
                    className="grid grid-cols-[minmax(150px,1.6fr)_74px_84px_84px_96px] items-center gap-2.5 border-b border-border px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono text-[11.5px] font-medium">{plan.itemCode}</span>
                        {plan.shortfall > 0 && (
                          <span className="inline-flex h-[17px] items-center rounded-full bg-warning/10 px-1.5 text-[10px] font-semibold text-warning">
                            Short by {plan.shortfall}
                          </span>
                        )}
                      </div>
                      <div
                        className="line-clamp-2 cursor-help text-[11px] leading-[1.35] text-muted-foreground"
                        title={`${plan.itemCode} · ${plan.itemName}`}
                      >
                        {plan.itemName}
                      </div>
                      <div className="mt-[5px] flex flex-wrap gap-1">
                        {mrsChipLabels(plan).map((label) => (
                          <span
                            key={label}
                            className="inline-flex h-[18px] items-center rounded-full bg-muted px-[7px] font-mono text-[10px] font-medium text-muted-foreground"
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>

                    <span className="text-[11px] text-muted-foreground">
                      {lineCount} {plural(lineCount, 'line', 'lines')}
                    </span>

                    <span className="text-right text-xs tabular-nums text-muted-foreground">
                      {mrsQty} {plan.unitOfMeasure}
                    </span>

                    <span className="text-right text-xs font-semibold tabular-nums">
                      {plan.total} {plan.unitOfMeasure}
                    </span>

                    <span className="flex flex-col items-end gap-0.5">
                      <button
                        type="button"
                        onClick={() => onViewBins(plan)}
                        title={binsTip}
                        aria-label={binsTip}
                        className="inline-flex h-[26px] items-center gap-1 whitespace-nowrap rounded-md border border-input bg-transparent px-[7px] text-[11px] font-semibold text-foreground"
                      >
                        <Warehouse className="h-3 w-3" />
                        View bins
                      </button>
                      <span className="whitespace-nowrap text-[10px] text-muted-foreground">
                        {chosen ? `${chosen} ${plural(chosen, 'bin', 'bins')} chosen` : 'Automatic'}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {submitError && (
          <div className="mx-5 mb-2 rounded-md border border-destructive/30 bg-destructive/10 p-2">
            <p className="text-xs text-destructive">{submitError}</p>
          </div>
        )}

        {/* Opaque, so content can never show through Cancel and the confirm button */}
        <div className="flex flex-none justify-end gap-2 border-t border-border bg-card px-5 pb-4 pt-[14px]">
          <Button
            variant="outline"
            className="h-[34px] rounded-lg px-[13px] text-[12.5px] font-medium"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            className="h-[34px] gap-[7px] rounded-lg px-[15px] text-[12.5px] font-semibold"
            onClick={onConfirm}
            // 14ykpqtv7nd — nothing left to create: a refused save re-reads the holds, and an item they leave with
            // nothing free drops out of `plans` while this dialog is still open. Its error stays on screen.
            disabled={isSubmitting || plans.length === 0}
          >
            {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
